import type { ChangeEvent, FC, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import {
  IconCarouselChevronLeft,
  IconWhiteCheckmark
} from "src/constants/icons";
import translate from "src/lib/translate";

import {
  CancelButton,
  CorrectionInput,
  InputCard,
  InputShell,
  RefineRow,
  SubmitButton,
  SubmitSpinner
} from "./MealRefineBar.styled";

const MAX_CORRECTION_LENGTH = 500;

interface MealRefineBarProps {
  onSubmit: (correction: string) => Promise<boolean>;
  onCancel: () => void;
}

const MealRefineBar: FC<MealRefineBarProps> = ({ onSubmit, onCancel }) => {
  const [correction, setCorrection] = useState("");
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const dismissOnOutsideTap = (event: MouseEvent): void => {
      if (pending || barRef.current?.contains(event.target as Node)) {
        return;
      }
      onCancel();
    };

    document.addEventListener("click", dismissOnOutsideTap);
    return () => document.removeEventListener("click", dismissOnOutsideTap);
  }, [onCancel, pending]);

  const trimmed = correction.trim();

  const submit = async (): Promise<void> => {
    if (!trimmed || pending) {
      return;
    }

    setPending(true);
    const refined = await onSubmit(trimmed);
    if (!refined) {
      setPending(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setCorrection(event.target.value.slice(0, MAX_CORRECTION_LENGTH));
    const field = event.target;
    field.style.height = "auto";
    field.style.height = `${field.scrollHeight}px`;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    event.preventDefault();
    if (trimmed) {
      void submit();
      return;
    }
    onCancel();
  };

  return (
    <RefineRow ref={barRef}>
      <CancelButton
        type="button"
        aria-label={translate("mealInsights.refine.cancel")}
        onClick={onCancel}
      >
        <IconCarouselChevronLeft />
      </CancelButton>
      <InputShell>
        <InputCard>
          <CorrectionInput
            ref={inputRef}
            rows={1}
            value={correction}
            maxLength={MAX_CORRECTION_LENGTH}
            readOnly={pending}
            enterKeyHint="send"
            aria-label={translate("mealInsights.refine.inputLabel")}
            placeholder={translate("mealInsights.refine.placeholder")}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          {(trimmed || pending) && (
            <SubmitButton
              type="button"
              disabled={pending}
              aria-label={translate(
                pending
                  ? "mealInsights.refine.pending"
                  : "mealInsights.refine.submit"
              )}
              onClick={() => void submit()}
            >
              {pending ? <SubmitSpinner /> : <IconWhiteCheckmark />}
            </SubmitButton>
          )}
        </InputCard>
      </InputShell>
    </RefineRow>
  );
};

export default MealRefineBar;
